import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Table } from 'reactstrap';
import EditButton from 'components/atoms/editButton';
import {
  getPatientPreferredContactValue,
  normalizePatientInfo,
  normalizeTimezone,
  getPatientCcmStatus,
  getPatientPcmStatus,
} from 'utils';
import { format, parse } from 'date-fns';
import common from 'constants/common';

const PatientInfo = ({ profile, handleEditClick, showEditButton = true }) => {
  const patient = normalizePatientInfo(profile);

  const DOB = useMemo(
    () =>
      patient.birthDate
        ? `${format(
            parse(patient.birthDate, 'MM-dd-yyyy', new Date()),
            common.dateFormats.short,
          )}`
        : '',
    [patient.birthDate],
  );

  const GLRE = useMemo(() => {
    const gender = patient.gender ?? ' - ';
    const language = patient.language ?? ' - ';
    const race = patient.race ?? ' - ';
    const ethnicity = patient.ethnicity ?? ' - ';

    return `${gender}/${language}/${race}/${ethnicity}`;
  }, [patient.gender, patient.language, patient.race, patient.ethnicity]);

  const MW = useMemo(() => {
    const martialStatus = patient.martialStatus ?? ' - ';
    const workingStatus = patient.workingStatus ?? ' - ';
    return `${martialStatus}/${workingStatus}`;
  });

  const emergencyContactName = useMemo(() => {
    if (
      (patient.emergencyContactFirstName === 'null' &&
        patient.emergencyContactLastName === 'null') ||
      (!patient.emergencyContactFirstName && !patient.emergencyContactLastName)
    )
      return ' - ';

    return `${patient.emergencyContactFirstName} ${patient.emergencyContactLastName}`;
  }, [patient.emergencyContactFirstName, patient.emergencyContactLastName]);

  const cityStateZip = useMemo(() => {
    if (!patient.city && !patient.state && !patient.zipPostalCode) return ' - ';

    return [`${patient.city}, ${patient.state}`, patient.zipPostalCode]
      .filter((el) => !!el && el !== ' ')
      .join(' ');
  }, [patient.city, patient.state, patient.zipPostalCode]);

  const ccmStatus = patient?.ccmStatus
    ? getPatientCcmStatus(patient.ccmStatus)?.label || ''
    : '';

  const pcmStatus = getPatientPcmStatus(patient?.pcmStatus);

  return (
    <Table
      borderless
      border="0"
      cellSpacing="0"
      cellPadding="0"
      style={{ wordBreak: 'break-word' }}
    >
      <thead>
        <tr>
          <th className="details-th" colSpan="2">
            <div className="pagination-container">
              <h5>Patient Info</h5>
              {showEditButton && <EditButton handleClick={handleEditClick} />}
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style={{ width: '120px' }}>
            <strong>Personal Info</strong>
          </td>
          <td>
            <span>
              {patient.firstName} {patient.middleName} {patient.lastName}
            </span>
            <span className="subheader">DOB:</span>
            <span>{DOB}</span>
            <span className="subheader">G/L/R/E:</span>
            <span>{GLRE}</span>
            <span className="subheader">M/W:</span>
            <span>{MW}</span>
          </td>
        </tr>
        <tr>
          <td>
            <strong>Account Info</strong>
          </td>
          <td>
            <span className="subheader">PatientID:</span>
            <span>{patient.guid}</span>
            <span className="subheader">RPM Status:</span>
            <span>{patient.status}</span>
            <span className="subheader">CCM Status:</span>
            <span>{ccmStatus}</span>
            <span className="subheader">PCM Status:</span>
            <span>{pcmStatus}</span>
          </td>
        </tr>
        <tr>
          <td>
            <strong>Contact Info</strong>
          </td>
          <td>
            <span className="subheader">Location:</span>
            <span>
              {patient.address} {cityStateZip}
            </span>
            <span className="subheader">Preferred Contact Method:</span>
            <span>
              {patient.preferredContactMethod}:{' '}
              {getPatientPreferredContactValue(patient)}
            </span>
            {patient.phone && (
              <>
                <span className="subheader">Primary Number:</span>
                <span>{patient.phone}</span>
              </>
            )}
            {patient.homeNumber && (
              <>
                <span className="subheader">Secondary Number:</span>
                <span>{patient.homeNumber}</span>
              </>
            )}
            <span className="subheader">Email:</span>
            <span>{patient.email}</span>
            <span className="subheader">Emergency Contact:</span>
            <span>{patient.emergencyContactNumber ?? ' - '}</span>
            <span className="subheader">Emergency Name:</span>
            <span>{emergencyContactName}</span>
            <span className="subheader">Time Zone:</span>
            <span>{normalizeTimezone(patient.timezone) ?? ' - '}</span>
          </td>
        </tr>
      </tbody>
    </Table>
  );
};

PatientInfo.propTypes = {
  profile: PropTypes.shape(),
  handleEditClick: PropTypes.func.isRequired,
  showEditButton: PropTypes.bool,
};

export default PatientInfo;
